import React from 'react';
import { useTranslation } from 'react-i18next';
import { Blueprint } from 'types/rest/assetCatalogTypes';
import BlazeTable from '../../../components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from '../../../components/blazeTable/BlazeTable.types';
import BlueprintThumb from './BlueprintThumb';
import BlueprintTag from './BlueprintTag';
import BlazeDataIcon from '../../../components/icons/BlazeDataIcon';

const PAGE_SIZE = 20;

interface Props {
  blueprints: Blueprint[];
  onOpen: (id: string) => void;
  onAddToCatalog: (id: string) => void;
}

const DESCRIPTION_CLAMP_STYLE: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};

const BlueprintsListTable = ({ blueprints, onOpen, onAddToCatalog }: Props) => {
  const { t } = useTranslation('blueprints');

  const columns: BlazeTableColDef[] = [
    {
      header: '',
      accessor: 'thumbnail',
      isSortable: false,
      width: 40,
      cellComponent: (bp: Blueprint) => (
        <BlueprintThumb
          name={bp.name}
          thumbnail={bp.thumbnail}
          fontSize={11}
          style={{ width: 32, height: 32, borderRadius: 6 }}
        />
      ),
    },
    {
      header: t('list.name'),
      accessor: 'name',
      width: 160,
      isSortable: true,
    },
    {
      header: t('list.description'),
      accessor: 'description',
      isSortable: false,
      cellComponent: (bp: Blueprint) => (
        <span style={DESCRIPTION_CLAMP_STYLE} title={bp.description ?? ''}>
          {bp.description ?? '—'}
        </span>
      ),
    },
    {
      header: t('list.tags'),
      accessor: 'tags',
      isSortable: false,
      width: 220,
      cellComponent: (bp: Blueprint) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {bp.tags.map(tag => (
            <BlueprintTag key={tag} tag={tag} />
          ))}
        </div>
      ),
    },
    {
      header: t('list.content'),
      accessor: 'transactionCount',
      isSortable: true,
      width: 80,
      cellComponent: (bp: Blueprint) => <span>{bp.transactionCount ?? bp.transactions.length}</span>,
    },
    {
      header: 'BlazeData',
      accessor: 'hasBlazeDataAccessor',
      isSortable: false,
      width: 80,
      cellComponent: (bp: Blueprint) => {
        return bp.hasBlazeData ? <BlazeDataIcon size={14} title="Includes a BlazeData dataset" /> : '-';
      },
    },
    {
      header: t('list.created'),
      accessor: 'createdAt',
      isSortable: true,
      width: 140,
      cellComponent: (bp: Blueprint) => <span>{bp.createdDate ?? '—'}</span>,
    },
    {
      header: t('list.actions'),
      accessor: 'id',
      width: 120,
      isSortable: false,
      cellComponent: (bp: Blueprint) => (
        <div className="text-right flex-table-actions text-right">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={e => {
              e.stopPropagation();
              onAddToCatalog(bp.id);
            }}
          >
            {t('card.add')}
          </button>
        </div>
      ),
    },
  ];

  const options: BlazeTableOptions = {
    columnDef: columns,
    selectable: false,
    showRowExpander: false,
    pageable: true,
    sortable: true,
    pageSize: PAGE_SIZE,
    minWidth: 900,
    tableName: 'blueprints-list',
    noResultsText: t('noResults'),
    onRowClick: (id: string) => onOpen(id),
  };

  if (blueprints.length === 0) {
    return <p className="text-muted">{t('noResults')}</p>;
  }

  return (
    <div>
      <BlazeTable data={blueprints} options={options} />
    </div>
  );
};

export default BlueprintsListTable;
